import {
	Icon,
	Message,
	MessageContent,
	MessageHeader,
	Popup,
	Segment,
	Table,
	TableBody,
	TableCell,
	TableHeader,
	TableHeaderCell,
	TableRow,
} from "semantic-ui-react";

import Modal from "../Modal/Modal";
import Button from "../UI/Button/Button";
import Preloader from "../UI/Preloader/Preloader";
import Select from "../UI/Select/Select";

import useEpcCompareUpdate from "../../hooks/useEpcCompareUpdate";
import Property from "../../rest/Property";

import "./CompareUpdateModal.scss";
import { DateTime } from "luxon";

interface CompareUpdateModalProps {
	property: Property;
	onClose: () => void;
	onSuccess: () => void;
}

export default function CompareUpdateModal({ property, onClose, onSuccess }: CompareUpdateModalProps) {
	const { loading, error, results, selected, setSelectedRrn, saving, saveError, save, refresh } = useEpcCompareUpdate(
		{ property, onSuccess }
	);

	const currentRrn: string | null = property.data.rrn ?? null;

	const options = results.map((result) => ({
		// Should be unique.
		key: result.rrn,
		value: result.rrn,
		text: result.address,
		description: result.lodgement_date,
	}));

	return (
		<Modal title="Compare & Update" className="compare-update-modal" closeIcon onClose={onClose}>
			{loading ? (
				<Preloader status="Searching the EPC register..." />
			) : error ? (
				<>
					<Message negative>{error}</Message>
					<Button variant="text" onClick={refresh}>
						Try again
					</Button>
				</>
			) : !results.length ? (
				<Message info>
					<MessageHeader>No Results</MessageHeader>
					<MessageContent>
						No EPC certificate could be found on the register matching this property.
					</MessageContent>
				</Message>
			) : (
				<>
					{results.length > 1 ? (
						<div className="address-select">
							<Select
								label="Matched address"
								placeholder="Select an address"
								size="full"
								options={options}
								value={selected?.rrn}
								onChange={(_, data) => setSelectedRrn(data.value as string)}
							/>
						</div>
					) : (
						// Only one result, so we can show the address with no dropdown.
						selected && (
							<p className="matched-address">
								Matched address: <strong>{selected.address}</strong>
							</p>
						)
					)}

					{selected && (
						<Table celled>
							<TableHeader>
								<TableRow>
									<TableHeaderCell>Field</TableHeaderCell>
									<TableHeaderCell>Current</TableHeaderCell>
									<TableHeaderCell>Latest</TableHeaderCell>
								</TableRow>
							</TableHeader>
							<TableBody>
								<TableRow>
									<TableCell>Lodgement Date</TableCell>
									<TableCell>{property.odcEpcLodgementDate}</TableCell>
									<TableCell>
										{/* Matches the format of the internal lodgement date. */}
										{DateTime.fromFormat(selected.lodgement_date, "yyyy-MM-dd").toFormat(
											"MMMM dd, yyyy"
										)}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell>EPC RRN</TableCell>
									<TableCell>{currentRrn ?? "None"}</TableCell>
									<TableCell>{selected.rrn}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell>
										<>
											Official UPRN{" "}
											<Popup
												content={
													"This will not overwrite your UPRN, but held in a field called O_UPRN"
												}
												position={"bottom center"}
												trigger={<Icon name="info circle" />}
											/>
										</>
									</TableCell>
									<TableCell>{property.data.o_uprn ?? "None"}</TableCell>
									<TableCell>{selected.uprn}</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					)}

					<p>
						<em>Last updated: {property.odcLastAcceptedSuggestion}</em>
					</p>

					{saveError && <Message negative>{saveError}</Message>}

					<Segment basic className="button-container">
						<Button variant="text" onClick={onClose}>
							Cancel
						</Button>
						<Button variant="contained" disabled={!selected || saving} onClick={save}>
							Save RRN
							{saving && <Preloader variant="inline" />}
						</Button>
					</Segment>
				</>
			)}
		</Modal>
	);
}
